'use client';

import { useEffect, useRef } from 'react';
import type { RoutePerfRoute } from '@/events/performance';
import { clientNetworkTag, isTelemetryActive } from '@/lib/telemetry';
import {
  beginRoutePerfVisit,
  type RoutePerfSample,
  type RoutePerfVisit,
} from '@/lib/telemetry/route-performance';
import { beaconVitals } from '@/lib/telemetry/vitals-beacon';

export interface UseRoutePerfOptions {
  /** Bounded route label; never pass an id, query string, or user identifier. */
  readonly name: RoutePerfRoute;
  /** `true` once the route's critical above-the-fold content is ready. */
  readonly isReady: boolean;
  /** Distinguishes visits when one mounted route boundary renders a new entity. Never exported. */
  readonly instanceKey?: string;
}

interface ActiveRoutePerfVisit {
  readonly name: RoutePerfRoute;
  readonly instanceKey: string | undefined;
  readonly visit: RoutePerfVisit;
  unmountTimer: number | null;
}

function emitRouteSample(sample: RoutePerfSample): void {
  beaconVitals({
    kind: 'route-perf',
    route: sample.route,
    navigationType: sample.navigationType,
    ttffMs: sample.ttffMs,
    routeReadyMs: sample.routeReadyMs,
    // Compatibility only: web TTI is the explicit route-ready clock, not the
    // retired Lighthouse Web TTI metric.
    ttiMs: sample.routeReadyMs,
    score: sample.score,
    category: sample.category,
    scoreVersion: sample.scoreVersion,
    network: clientNetworkTag(),
  });
}

function reportUnmounted(): void {
  beaconVitals({ kind: 'route-perf-health', reason: 'unmounted', network: clientNetworkTag() });
}

function observeFirstContentfulPaint(visit: RoutePerfVisit): (() => void) | undefined {
  const existing = performance.getEntriesByName('first-contentful-paint')[0];
  if (existing !== undefined) {
    visit.markPaint(existing.startTime);
    return undefined;
  }
  if (typeof PerformanceObserver === 'undefined') return undefined;
  const observer = new PerformanceObserver((entries) => {
    const entry = entries
      .getEntries()
      .find((candidate) => candidate.name === 'first-contentful-paint');
    if (entry === undefined) return;
    visit.markPaint(entry.startTime);
    observer.disconnect();
  });
  try {
    observer.observe({ type: 'paint', buffered: true });
    return (): void => observer.disconnect();
  } catch {
    // SILENT-CATCH: unsupported PerformanceObserver paint entries leave the sample incomplete.
    observer.disconnect();
    return undefined;
  }
}

/**
 * Declarative per-route RUM boundary. It is a no-op outside live Vercel
 * telemetry and emits one final sample only after both paint and readiness.
 */
export function useRoutePerf({ name, isReady, instanceKey }: UseRoutePerfOptions): void {
  const activeRef = useRef<ActiveRoutePerfVisit | null>(null);

  useEffect(() => {
    if (!isTelemetryActive()) return;

    let active = activeRef.current;
    if (active?.name === name && active.instanceKey === instanceKey) {
      if (active.unmountTimer !== null) {
        window.clearTimeout(active.unmountTimer);
        active.unmountTimer = null;
      }
    } else {
      active = {
        name,
        instanceKey,
        visit: beginRoutePerfVisit(name, emitRouteSample),
        unmountTimer: null,
      };
      activeRef.current = active;
    }

    const visit = active.visit;
    const disconnect =
      visit.navigationType === 'soft' ? undefined : observeFirstContentfulPaint(visit);
    return (): void => {
      disconnect?.();
      // Strict Mode re-runs effects immediately; defer finalisation one task so
      // its setup can cancel this while a real unmount remains observable.
      active.unmountTimer = window.setTimeout(() => {
        if (!visit.isComplete()) reportUnmounted();
        if (activeRef.current === active) activeRef.current = null;
      }, 0);
    };
  }, [instanceKey, name]);

  useEffect(() => {
    const active = activeRef.current;
    if (isReady && active?.name === name && active.instanceKey === instanceKey) {
      active.visit.markReady(performance.now());
    }
  }, [instanceKey, isReady, name]);
}
